import React, { useState, useEffect } from 'react';
import { Link, Redirect, useHistory } from 'react-router-dom';
import userStore from '../../../Stores/User';
import pricingStore from '../../../Stores/PricingStore';
import { observer } from 'mobx-react';
import { loadStripe } from '@stripe/stripe-js';
import mixpanel from 'mixpanel-browser';
import loadingIcon from '../../../Assets/loading.gif';
import { validateCoupon, cancelSetupIntent } from '../../../audiblogsApi';
import queryString from 'query-string';
import CheckoutForm from '../../CheckoutForm';
import PricingCards from '../../../Components/PricingCards';
import firebase from "../../../firebase";
import { isMobile, isDesktop, isSafari, isChromium, isAndroid, isIOS, isChrome } from 'react-device-detect';

const STRIPE_API_KEY = process.env.REACT_APP_STRIPE_API_KEY;
const stripePromise = loadStripe(STRIPE_API_KEY);

// A/B Test helper function
const getPaywallVariant = (params) => {
  // Check for forced variant first
  const forcedVariant = params.paywall?.toLowerCase();
  if (forcedVariant === 'a' || forcedVariant === 'b') {
    return forcedVariant;
  }
  
  // Check if user already has a variant assigned
  const storedVariant = localStorage.getItem('paywall_variant');
  if (storedVariant) {
    return storedVariant;
  }
  
  // Assign variant based on 30% B, 70% A split
  const random = Math.random();
  const variant = random < 0.3 ? 'b' : 'a';
  localStorage.setItem('paywall_variant', variant);
  return variant;
};

const InitialCheckoutForm = observer(({ onCouponSubmit, couponError, couponSuccess, onAddPaymentDetails, couponCode, setCouponCode, isLoading }) => {
  return (
    <div>
      {/* Coupon Input */}
      <div style={{ 
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '15px'
      }}>
        <input
          type="text"
          value={couponCode}
          onChange={(e) => setCouponCode(e.target.value)}
          placeholder="Coupon code"
          style={{
            flex: 1,
            padding: '8px 12px',
            backgroundColor: 'rgba(24,15,34,1)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.3)',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        />
        <button
          onClick={() => onCouponSubmit(couponCode)}
          disabled={isLoading}
          style={{
            padding: '8px 12px',
            backgroundColor: 'transparent',
            color: '#fff',
            border: '1px solid #fff',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            opacity: isLoading ? 0.7 : 1
          }}
        >
          {isLoading ? 'Applying...' : 'Apply'}
        </button>
      </div>

      {/* Coupon Messages */}
      {couponError && (
        <div style={{ 
          color: '#df1b41', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✕</span> {couponError}
        </div>
      )}
      
      {couponSuccess && (
        <div style={{ 
          color: '#4ade80', 
          fontSize: '14px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '5px'
        }}>
          <span style={{ fontWeight: 'bold' }}>✓</span> {couponSuccess}
        </div>
      )}

      {/* Add Payment Details Button */}
      <div 
        onClick={onAddPaymentDetails}
        role="button"
        style={{
          width: '100%',
          padding: '12px',
          backgroundColor: '#3b82f6 !important',
          background: '#3b82f6 !important',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px',
          textAlign: 'center',
          userSelect: 'none'
        }}
      >
        Add Payment Details
      </div>

      {/* Terms - Only show in InitialCheckoutForm */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '20px',
        fontSize: '12px',
        color: '#666'
      }}>
        By continuing, you agree to our{' '}
        <a 
          href="/terms" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#fff', textDecoration: 'underline' }}
        >
          Terms of Service
        </a>
      </div>
    </div>
  );
});

const ChooseVoice = observer((props) => {
  const [quality, setQuality] = useState('Unreal');
  const [subscriptionType, setSubscriptionType] = useState(() => {
    // Get forced variant from URL params first, then fallback to mixpanel
    const forcedVariant = queryString.parse(props.location.search).variant?.toLowerCase();
    const landingPage = mixpanel.get_property('utm_landingpage');
    const isVariantB = forcedVariant === 'b' || landingPage === 'landing_b';
    if (isVariantB) return 'pro-monthly';
    return pricingStore.defaultSubscriptionType;
  });
  const [sessionId, setSessionId] = useState(null);
  const params = queryString.parse(props.location.search);
  const app = params.app || '';
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [showPaymentElement, setShowPaymentElement] = useState(false);
  const [paymentKey, setPaymentKey] = useState(0);
  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [landingVariant, setLandingVariant] = useState('A');
  const [customToken, setCustomToken] = useState(params.custom_token);
  const [device, setDevice] = useState(null);
  const [signedInWithCustomToken, setSignedInWithCustomToken] = useState(false);
  const [paywallVariant, setPaywallVariant] = useState(null);
  const history = useHistory();

  // Helper function to estimate audio duration
  const estimateAudioDuration = (charCount) => {
    return Math.round((charCount / 10000) * 10.5);
  };

  useEffect(() => {
    if (!userStore.user) {
      setShouldRedirect(true);
      return;
    }
    if (userStore.user && userStore.user.vip_id) {
      setShouldRedirect(true);
    }

    // Initialize A/B test
    const variant = getPaywallVariant(params);
    setPaywallVariant(variant);
    
    // Track paywall variant visit
    mixpanel.track('paywall page a / b visited', {
      variant: variant,
      forced: !!params.paywall
    });

    // Check for forced variant in URL params first, then fallback to mixpanel
    const forcedVariant = params.variant?.toLowerCase();
    if (forcedVariant === 'a' || forcedVariant === 'b') {
      setLandingVariant(forcedVariant.toUpperCase());
      if (forcedVariant === 'b') {
        setSubscriptionType('pro-monthly');
      }
    } else {
      // Get landing page variant from mixpanel
      const landingPage = mixpanel.get_property('utm_landingpage');
      console.log('landingPage', landingPage);
      if (landingPage === 'landing_b') {
        setLandingVariant('A');
        setSubscriptionType('pro-monthly');
      }
    }

    // Track pricing display variant
    mixpanel.people.set({
      checkoutPricing: pricingStore.showOnlyPro ? 'pro_only' : 'all_plans'
    });
    mixpanel.track('Checkout Pricing Variant Shown', {
      variant: pricingStore.showOnlyPro ? 'pro_only' : 'all_plans'
    });

    // Simulate initial loading
    setTimeout(() => {
      setIsInitialLoading(false);
    }, 1500);
  }, [userStore.user, params.variant, params.paywall]);

  // Signup tracking useEffect
  useEffect(() => {
    if (customToken && !userStore.isSignedIn) {
      firebase.auth().signInWithCustomToken(customToken)
        .then((userCredential) => {
          // Signed in
          var user = userCredential.user;
          setSignedInWithCustomToken(true);
        })
    } else if (!customToken && userStore.isSignedIn === false && userStore.initialized){
      history.push('/signin?redirected_from=onboarding/choosevoice');
    }
  }, [customToken, userStore.isSignedIn, userStore.initialized]);

  // Device detection useEffect
  useEffect(() => {
    if (isIOS){
      setDevice('ios')
    } else if (isAndroid){
      setDevice('android')
    } else {
      setDevice('desktop')
    }
  },[]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showPaymentElement) {
      setShowPaymentElement(true);
    }
  }, [showPaymentElement]);

  const handleCouponSubmit = async (code) => {
    if (!code.trim()) return;
    setIsLoading(true);
    try {
      const result = await validateCoupon(userStore.idToken, code.trim());
      if (result.error) {
        setCouponError(result.error);
      } else if (result.valid && result.coupon_details) {
        setCouponSuccess(formatCouponMessage(result.coupon_details));
        setCouponCode(code);
      } else {
        setCouponError('Invalid coupon code');
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
    } finally {
      setIsLoading(false);
    }
  };

  const formatCouponMessage = (couponDetails) => {
    let message = '';
    if (couponDetails.percent_off) {
      message = `${couponDetails.percent_off}% off`;
    } else if (couponDetails.amount_off) {
      const amount = (couponDetails.amount_off / 100).toFixed(2);
      const currency = couponDetails.currency.toUpperCase();
      message = `${currency} ${amount} off`;
    }

    if (couponDetails.duration === 'repeating' && couponDetails.duration_in_months) {
      message += ` for ${couponDetails.duration_in_months} months`;
    } else if (couponDetails.duration === 'once') {
      message += ` (one-time)`;
    } else if (couponDetails.duration === 'forever') {
      message += ` forever`;
    }

    if (couponDetails.restrictions?.minimum_amount) {
      const minAmount = (couponDetails.restrictions.minimum_amount / 100).toFixed(2);
      const currency = couponDetails.restrictions.minimum_amount_currency.toUpperCase();
      message += ` (min. ${currency} ${minAmount})`;
    }

    return message || 'Coupon applied successfully!';
  };

  const handlePaymentDetails = () => {
    setPaymentKey(prev => prev + 1);
    setShowPaymentElement(true);
  };

  const handleChangePlan = async () => {
    if (setupIntentId) {
      setIsLoading(true);
      try {
        await cancelSetupIntent(userStore.idToken, setupIntentId);
        setSetupIntentId(null);
        setShowPaymentElement(false);
        localStorage.removeItem('lastSetupIntentId');
        setPaymentKey(prev => prev + 1);
      } catch (error) {
        console.error('Error canceling setup intent:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubscriptionTypeChange = (newType) => {
    if (!showPaymentElement) {
      setSubscriptionType(newType);
    }
  };

  if (shouldRedirect) {
    const searchParams = new URLSearchParams();
    if (app) searchParams.set('app', app);
    if (params.variant) searchParams.set('variant', params.variant);
    const queryString = searchParams.toString();
    return <Redirect to={userStore.user ? `quickstart?${queryString}` : `/signin?redirected_from=signup&${queryString}`} />;
  }

  if (isInitialLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#180f22',
        zIndex: 1000
      }}>
        <div style={{
          position: 'relative',
          width: '120px',
          height: '120px',
          marginBottom: '20px'
        }}>
          <img 
            src="/logo192.png" 
            alt="Audioread" 
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              animation: 'pulse 2s infinite'
            }}
          />
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '140px',
            height: '140px',
            border: '3px solid rgba(255,255,255,0.1)',
            borderTopColor: 'rgb(255 143 65)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; }
              50% { transform: scale(1.1); opacity: 0.8; }
              100% { transform: scale(1); opacity: 1; }
            }
            @keyframes spin {
              0% { transform: translate(-50%, -50%) rotate(0deg); }
              100% { transform: translate(-50%, -50%) rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  const firstName = userStore.user?.displayName?.split(' ')[0];
  const welcomeMessage = firstName ? `👋 Hi ${firstName},\ntry a premium plan free for 7 days` : 'Start your free trial';

  // Variant B content - Speechify-inspired layout
  if (paywallVariant === 'b') {
    return (
      <div className="center-static" style={{ 
        minHeight: '100vh',
        width: '100%',
        maxWidth: '1200px',
        padding: '20px',
        boxSizing: 'border-box',
        overflow: 'auto',
        margin: '0 auto'
      }}>
        {/* Header with Logo */}
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <img 
            src="/logo192.png" 
            alt="Audioread" 
            style={{
              width: '80px',
              height: '80px',
              marginBottom: '10px'
            }}
          />
        </div>

        {/* Two Column Layout */}
        <div style={{
          display: 'flex',
          gap: '40px',
          alignItems: 'flex-start',
          flexWrap: 'wrap'
        }}>
          {/* Left Column - Main Offer */}
          <div style={{ flex: 1.3, minWidth: '300px', textAlign: 'left', alignItems: 'flex-start', display: 'flex', flexDirection: 'column' }}>
            {/* Headline and Pricing */}
            <div style={{ marginBottom: '30px' }}>
              <h1 style={{ 
                fontSize: '36px',
                fontWeight: 'bold',
                color: '#fff',
                marginBottom: '8px'
              }}>
                Try Audioread Premium
              </h1>
              <div style={{ 
                fontSize: '20px', 
                color: '#4ade80', 
                fontWeight: 'bold',
                marginBottom: '8px'
              }}>
                free for 7 days
              </div>
              <div style={{ 
                fontSize: '16px', 
                color: '#9ca3af',
                marginBottom: '10px'
              }}>
                Then $69.00/year, only $1.35/week
              </div>
            </div>

            {/* Features List */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Convert any link, file, email, text or rss feed into 100+ human voices instantly</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Listen on your podcast player or any browser - No app needed!</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Adjustable playback speed</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>80+ languages - <a href="https://help.audioread.com/en/articles/9854895-can-audioread-handle-non-english-languages" target="_blank" rel="noopener noreferrer">See full list</a></span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Download your audio</span>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Get AI summaries of your content</span>
              </div>
              <div style={{ 
                display: 'flex',
                alignItems: 'center', 
                marginBottom: '12px',
                fontSize: '16px'
              }}>
                <span style={{ color: '#4ade80', marginRight: '12px', fontSize: '18px' }}>✓</span>
                <span style={{ color: '#fff' }}>Human support</span>
              </div>
            </div>

            {/* Start for Free Button */}
            <div style={{ 
              width: '100%',
              backgroundColor: 'rgba(24,15,34,0.5)',
              borderRadius: '8px'
            }}>
              <button
                onClick={() => {
                  // Set subscription type to pro-monthly for the trial
                  setSubscriptionType('pro-monthly');
                  setPaymentKey(prev => prev + 1);
                  setShowPaymentElement(true);
                }}
                style={{
                  width: '100%',
                  padding: '20px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                }}
              >
                Start for Free
              </button>
              {/* Social Proof - Started Premium */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '20px',
              marginTop: '20px',
              marginLeft: '20px',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#fbbf24',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😊</div>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#a855f7',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😄</div>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px'
                }}>😃</div>
              </div>
              <span style={{ color: '#9ca3af', fontSize: '14px' }}>3,781 started Premium this week</span>
            </div>
              
              {/* Payment Element Modal */}
              {showPaymentElement && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000,
                  padding: '20px'
                }}>
                  <div style={{
                    backgroundColor: '#180f22',
                    borderRadius: '16px',
                    padding: '30px',
                    maxWidth: '500px',
                    width: '100%',
                    border: '1px solid rgba(255,255,255,0.1)',
                    position: 'relative'
                  }}>
                    {/* Close Button */}
                    <button
                      onClick={() => {
                        setShowPaymentElement(false);
                        setPaymentKey(prev => prev + 1);
                      }}
                      style={{
                        position: 'absolute',
                        top: '15px',
                        right: '20px',
                        background: 'none',
                        border: 'none',
                        color: '#9ca3af',
                        fontSize: '24px',
                        cursor: 'pointer',
                        padding: '0',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      ×
                    </button>
                    
                    {/* Modal Header */}
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                      <p style={{ 
                        color: '#9ca3af', 
                        fontSize: '16px',
                        marginBottom: '20px'
                      }}>
                        Choose your billing cycle and enter payment details to begin your risk-free trial.
                      </p>
                    </div>

                    {/* Billing Selection */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '12px', 
                      marginBottom: '30px'
                    }}>
                      {/* Monthly Option */}
                      <div 
                        onClick={() => setSubscriptionType('pro-monthly')}
                        style={{
                          flex: 1,
                          padding: '16px',
                          backgroundColor: subscriptionType === 'pro-monthly' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(24,15,34,0.5)',
                          border: subscriptionType === 'pro-monthly' ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold', 
                          color: '#fff',
                          marginBottom: '4px'
                        }}>
                          Monthly
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#9ca3af'
                        }}>
                          $9.99/month
                        </div>
                      </div>

                      {/* Yearly Option */}
                      <div 
                        onClick={() => setSubscriptionType('pro-yearly')}
                        style={{
                          flex: 1,
                          padding: '16px',
                          backgroundColor: subscriptionType === 'pro-yearly' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(24,15,34,0.5)',
                          border: subscriptionType === 'pro-yearly' ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.2)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'center',
                          transition: 'all 0.2s'
                        }}
                      >
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 'bold', 
                          color: '#fff',
                          marginBottom: '4px'
                        }}>
                          Yearly
                        </div>
                        <div style={{ 
                          fontSize: '14px', 
                          color: '#9ca3af'
                        }}>
                          $5.83/month
                        </div>
                        <div style={{ 
                          fontSize: '12px', 
                          color: '#4ade80',
                          marginTop: '2px'
                        }}>
                          Save 42%
                        </div>
                      </div>
                    </div>
                    
                    {/* Payment Form */}
                    <CheckoutForm 
                      planType={subscriptionType}
                      key={paymentKey}
                      couponCode={couponCode}
                      onSetupIntentCreated={(setupIntentId) => {
                        setSetupIntentId(setupIntentId);
                        
                        // Track subscription setup
                        mixpanel.track('Quickstart: Subscription setup done', {
                          completed: true,
                          plan: 'pro',
                          billing: subscriptionType.includes('yearly') ? 'yearly' : 'monthly',
                          paywall: paywallVariant
                        });
                      }}
                      onShowPaymentElement={setShowPaymentElement}
                    />
                  </div>
                </div>
              )}
            </div>


          </div>

          {/* Right Column - Social Proof Reviews */}
          <div style={{ 
            flex: 2, 
            minWidth: '300px'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '12px',
              maxWidth: '100%'
            }}>
              {/* Review Card 1 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#8b5cf6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😊</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Perfect for grad students! Saves me hours."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Helen W
                </div>
              </div>

              {/* Review Card 2 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#3b82f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😄</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "100x better than Pocket"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Aidan Torrence
                </div>
              </div>

              {/* Review Card 3 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😃</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "My TLDR Audio button. Highly recommend!"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Arvin Garcia
                </div>
              </div>

              {/* Review Card 4 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f59e0b',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😁</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Amazing! Voice quality comparable to Audible."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Johanna Quinn
                </div>
              </div>

              {/* Review Card 5 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ef4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🤩</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Fucking fantastic. Great voice quality."
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Brent Maxwell
                </div>
              </div>

              {/* Review Card 8 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#84cc16',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🥳</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Amazeballs"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Geffrey van der Bos
                </div>
              </div>

              {/* Review Card 9 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#ec4899',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>😍</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Have been waiting for something like this for so long. YES"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  Elianna Goldstein
                </div>
              </div>

              {/* Review Card 10 */}
              <div style={{
                padding: '12px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minHeight: '120px'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: '#f97316',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '8px'
                }}>🤩</div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#fbbf24', fontSize: '12px' }}>★★★★★</span>
                </div>
                <div style={{ color: '#e5e7eb', fontSize: '12px', lineHeight: '1.3', marginBottom: '4px' }}>
                  "Absolutely Amazing and will save alot of times spent on reading"
                </div>
                <div style={{ color: '#9ca3af', fontSize: '10px', fontWeight: 'bold' }}>
                  @astonish2c
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Accordion Section */}
        <div style={{
          width: '80%',
          margin: '40px auto 30px',
          padding: '20px',
          backgroundColor: 'rgba(24,15,34,0.3)',
          borderRadius: '12px',
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          <h3 style={{ 
            fontSize: '20px', 
            color: '#fff', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Frequently Asked Questions
          </h3>
          
          {/* FAQ Item 1 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq1-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How does Audioread work?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq1-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Simply paste any link, upload files, forward emails, or add RSS feeds - Audioread instantly converts them to high-quality audio. Listen directly in your browser or sync to your favorite podcast player for seamless offline listening.
            </div>
          </div>

          {/* FAQ Item 2 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq2-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How can I use Audioread?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq2-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Setup takes just 5 minutes: install our browser extension, configure shortcuts, add RSS feeds, and connect your podcast app. After that, adding content is completely automatic or requires just 2 clicks - your audio appears in your podcast player instantly, ready to listen.
            </div>
          </div>

          {/* FAQ Item 3 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq3-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              What's special about Audioread?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq3-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Audioread combines all the premium features you'd expect from other services - but at the lowest cost for high-quality AI voices. Plus, we're the only service that lets you listen through your podcast player, giving you access to all your favorite podcast features like speed control, sleep timers, and offline downloads.
            </div>
          </div>

          {/* FAQ Item 4 */}
          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => {
                const content = document.getElementById('faq4-content');
                content.style.display = content.style.display === 'none' ? 'block' : 'none';
              }}
              style={{
                width: '100%',
                padding: '16px',
                backgroundColor: 'rgba(24,15,34,0.5)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '16px',
                fontWeight: 'bold',
                textAlign: 'left',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              How to cancel subscription?
              <span style={{ fontSize: '20px' }}>▼</span>
            </button>
            <div id="faq4-content" style={{
              padding: '16px',
              backgroundColor: 'rgba(24,15,34,0.3)',
              border: '1px solid rgba(139, 92, 246, 0.2)',
              borderTop: 'none',
              borderRadius: '0 0 8px 8px',
              color: '#e5e7eb',
              fontSize: '14px',
              lineHeight: '1.5',
              display: 'none'
            }}>
              Your subscription is completely risk-free. Cancel anytime with no questions asked - you'll keep access until the end of your current billing period. No hidden fees, no complicated cancellation process.
            </div>
          </div>
        </div>

        {/* Terms */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '30px',
          fontSize: '12px',
          color: '#666'
        }}>
          By continuing, you agree to our{' '}
          <a 
            href="/terms" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ color: '#fff', textDecoration: 'underline' }}
          >
            Terms of Service
          </a>
        </div>
      </div>
    );
  }

  // Variant A (original content)
  return (
    <div className="center-static" style={{ 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      padding: '20px',
      boxSizing: 'border-box',
      overflow: 'auto'
    }}>
      <h1 className="headline" style={{ marginBottom: '0px', whiteSpace: 'pre-line' }}>
        {welcomeMessage}
      </h1>

      {/* Social Proof */}
      <div style={{
        textAlign: 'center',
        margin: '20px 0',
        padding: '15px',
        backgroundColor: 'rgba(255,255,255,0.05)',
        borderRadius: '8px',
        fontSize: '14px'
      }}>
        <div style={{ marginBottom: '10px' }}>⭐️ Join 100,000+ happy listeners</div>
        <div style={{ opacity: 0.8 }}>"This app has completely changed how I consume content" - Sarah K.</div>
      </div>

      {/* Urgency */}
      {/* {!pricingStore.showOnlyPro && (
        <div style={{
          textAlign: 'center',
          margin: '20px 0',
          padding: '15px',
          backgroundColor: 'rgba(255,143,65,0.1)',
          borderRadius: '8px',
          fontSize: '14px',
          border: '1px solid rgba(255,143,65,0.3)'
        }}>
          <div style={{ color: 'rgb(255 143 65)', fontWeight: 'bold', fontSize: '16px' }}>🔥 EXCLUSIVE 1-TIME OFFER</div>
          <div style={{ marginTop: '8px', opacity: 0.9 }}>
            <div style={{ marginBottom: '4px' }}>🎁 7-day free trial + 50% off your first payment</div>
            <div style={{ fontSize: '13px', opacity: 0.8 }}>Use code <span style={{ fontWeight: 'bold', color: 'rgb(255 143 65)' }}>HALFOFF</span> - Works on both monthly & yearly plans!</div>
            <div style={{ fontSize: '12px', marginTop: '6px', fontStyle: 'italic', opacity: 0.7 }}>This offer is only available when starting your trial now. If you sign up later, you'll miss out on the 50% discount.</div>
          </div>
        </div>
      )} */}

      <h2 className="subheadline" style={{ whiteSpace: 'pre-line' }}>Choose your plan</h2>
      
      {/* Plan Selection Cards */}
      <PricingCards 
        onPlanSelect={handleSubscriptionTypeChange}
        selectedPlan={subscriptionType}
        showPaymentElement={showPaymentElement}
        estimateAudioDuration={estimateAudioDuration}
      />

      {/* Change Plan Button */}
      {showPaymentElement && (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <button 
            onClick={handleChangePlan}
            disabled={isLoading}
            style={{
              padding: '8px 20px',
              backgroundColor: 'transparent',
              border: '1px solid #fff',
              borderRadius: '4px',
              color: '#fff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              width: 'fit-content',
              fontSize: '14px',
              opacity: isLoading ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              margin: '0 auto'
            }}
          >
            {isLoading ? (
              <>
                <img src={loadingIcon} alt="Loading..." style={{ width: '16px', height: '16px' }} />
                Changing Plan...
              </>
            ) : (
              'Change Plan'
            )}
          </button>
        </div>
      )}

      {/* Coupon and Payment Section */}
      <div style={{ 
        maxWidth: '100%', 
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px',
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {subscriptionType === 'free' ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ maxWidth: '300px', margin: '0 auto' }}>
              <button
                onClick={() => {
                  mixpanel.track('Quickstart: Subscription setup done', {
                    completed: true,
                    plan: 'free',
                    paywall: paywallVariant
                  });
                  mixpanel.people.set_once({
                    subscription_plan: 'free',
                    registered: true
                  });
                  props.history.push('/add');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  textAlign: 'center'
                }}
              >
                Continue to App
              </button>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '20px',
              fontSize: '12px',
              color: '#666'
            }}>
              By continuing, you agree to our{' '}
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#fff', textDecoration: 'underline' }}
              >
                Terms of Service
              </a>
            </div>
          </div>
        ) : (
          <CheckoutForm 
            planType={subscriptionType} 
            key={paymentKey}
            couponCode={couponCode}
            onSetupIntentCreated={(setupIntentId) => {
              setSetupIntentId(setupIntentId);
              const plan = subscriptionType.includes('pro') ? 'pro' : 'basic';
              
              // Track subscription setup
              mixpanel.track('Quickstart: Subscription setup done', {
                completed: true,
                plan: plan,
                billing: subscriptionType.includes('yearly') ? 'yearly' : 'monthly',
                paywall: paywallVariant
              });
              
            }}
            onShowPaymentElement={setShowPaymentElement}
          />
        )}
      </div>

      {/* Money Back Guarantee */}
      <div style={{
        textAlign: 'center',
        margin: '20px auto',
        maxWidth: '600px',
        padding: '15px',
        backgroundColor: 'rgba(76,175,80,0.1)',
        borderRadius: '8px',
        fontSize: '14px',
        border: '1px solid rgba(76,175,80,0.3)'
      }}>
        <div style={{ color: '#4CAF50', fontWeight: 'bold', marginBottom: '5px' }}>🔒 100% Money-Back Guarantee</div>
        <div style={{ opacity: 0.8 }}>If you're not satisfied within 30 days, we'll refund your payment. No questions asked.</div>
      </div>

      {/* FAQ Section */}
      <div style={{
        maxWidth: '600px',
        margin: '20px auto',
        padding: '20px',
        backgroundColor: 'rgba(24,15,34,0.5)',
        borderRadius: '8px'
      }}>
        <h3 style={{ marginBottom: '15px', fontSize: '18px' }}>Frequently Asked Questions</h3>
        
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What are the article length limits?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>
            Free users can convert articles up to 10,000 characters (~{estimateAudioDuration(10000)} minutes of audio). 
            Paid plans (Basic and Pro) support articles up to 100,000 characters (~{estimateAudioDuration(100000)} minutes of audio).
          </div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What happens after the 7-day trial?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Your subscription will automatically continue at the regular price. You can cancel anytime before the trial ends.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Can I cancel anytime?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, you can cancel your subscription at any time. Your plan will stay active until the end of the current billing period.</div>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>What payment methods do you accept?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>We accept all major credit cards, including Visa, Mastercard, American Express, Discover, and Apple/Google Pay.</div>
        </div>

        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Is my payment information secure?</div>
          <div style={{ opacity: 0.8, fontSize: '14px' }}>Yes, we use Stripe for secure payment processing. We never store your credit card information.</div>
        </div>
      </div>
            {/* Stick with Free Button - Moved below payment section */}
            <div
        style={{ 
          maxWidth: 300, 
          margin: '20px auto', 
          textAlign: 'center',
          opacity: 0.7,
          fontSize: '14px'
        }}
      >
        {/* <span 
          style={{ 
            cursor: 'pointer',
            textDecoration: 'underline',
            color: '#fff'
          }}
          onClick={() => {
            mixpanel.track('Quickstart: Subscription setup done - free version', {
              completed: true,
              plan: 'free'
            });
            mixpanel.people.set_once({
              subscription_plan: 'free',
              registered: true
            });
            props.history.push('/add');
          }}
        >
          Continue with limited free version
        </span> */}
      </div>
    </div>
  );
});

export default ChooseVoice;
